/**
 * Window tinting services (e.g. "Full Window Tint") let the customer pick a
 * tint percentage per piece of glass. The service payload exposes
 * `is_window_tinting` with the available `glass_types` and `tint_percentages`,
 * and the chosen pairs travel to `/v1/shop` as `tint_details`.
 */

export type GlassType = {
  id: number;
  name: string;
  sequence?: number;
};

export type TintPercentage = {
  id: number;
  name: string;
  percentage?: number;
};

export type TintOptions = {
  glassTypes: GlassType[];
  tintPercentages: TintPercentage[];
};

/** Chosen tint percentage keyed by glass type id. */
export type TintSelections = Record<number, number>;

export type TintDetail = {
  glass_type_id: number;
  tint_percentage_id: number;
};

function positiveId(value: unknown): number | null {
  const id = Number(value);
  return Number.isInteger(id) && id > 0 ? id : null;
}

function parseGlassTypes(value: unknown): GlassType[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((row): GlassType | null => {
      if (!row || typeof row !== "object") return null;
      const raw = row as Record<string, unknown>;
      const id = positiveId(raw.id);
      const name = String(raw.name ?? "").trim();
      if (id == null || !name) return null;

      const sequence = Number(raw.sequence);
      return {
        id,
        name,
        sequence: Number.isFinite(sequence) ? sequence : 0,
      };
    })
    .filter((row): row is GlassType => row !== null)
    .sort(
      (a, b) =>
        (a.sequence ?? 0) - (b.sequence ?? 0) || a.name.localeCompare(b.name)
    );
}

function parseTintPercentages(value: unknown): TintPercentage[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((row): TintPercentage | null => {
      if (!row || typeof row !== "object") return null;
      const raw = row as Record<string, unknown>;
      const id = positiveId(raw.id);
      const name = String(raw.name ?? "").trim();
      if (id == null || !name) return null;

      const percentage = Number(raw.percentage);
      return {
        id,
        name,
        percentage: Number.isFinite(percentage) ? percentage : undefined,
      };
    })
    .filter((row): row is TintPercentage => row !== null)
    .sort((a, b) => (a.percentage ?? 0) - (b.percentage ?? 0));
}

/** True only when the service is a tint service *and* both option lists exist. */
export function isWindowTintingService(item: unknown): boolean {
  if (!item || typeof item !== "object") return false;
  const raw = item as Record<string, unknown>;
  if (raw.is_window_tinting !== true) return false;

  const { glassTypes, tintPercentages } = getTintOptions(item);
  return glassTypes.length > 0 && tintPercentages.length > 0;
}

export function getTintOptions(item: unknown): TintOptions {
  if (!item || typeof item !== "object") {
    return { glassTypes: [], tintPercentages: [] };
  }

  const raw = item as Record<string, unknown>;
  return {
    glassTypes: parseGlassTypes(raw.glass_types),
    tintPercentages: parseTintPercentages(raw.tint_percentages),
  };
}

/**
 * Flatten the per-glass choices into the `tint_details` array. Glass types the
 * customer left unset are dropped, so an empty result means nothing was chosen.
 */
export function buildTintDetails(selections: TintSelections): TintDetail[] {
  return Object.entries(selections)
    .map(([glassTypeId, tintPercentageId]) => {
      const glass = positiveId(glassTypeId);
      const tint = positiveId(tintPercentageId);
      if (glass == null || tint == null) return null;
      return { glass_type_id: glass, tint_percentage_id: tint };
    })
    .filter((detail): detail is TintDetail => detail !== null);
}

/** Drop choices for glass types the service no longer offers. */
export function pruneTintSelections(
  selections: TintSelections,
  glassTypes: GlassType[]
): TintSelections {
  const allowed = new Set(glassTypes.map((glass) => glass.id));
  const next: TintSelections = {};

  for (const [glassTypeId, tintPercentageId] of Object.entries(selections)) {
    const glass = positiveId(glassTypeId);
    if (glass != null && allowed.has(glass)) {
      next[glass] = tintPercentageId;
    }
  }

  return next;
}

/**
 * Human-readable "Glass: 15%" pairs for a cart line, so the customer can review
 * the tint choices before paying. Names come from the line's own option lists,
 * or from `*_name` fields when the server already resolved them.
 */
export function formatTintSummary(line: unknown): string[] {
  if (!line || typeof line !== "object") return [];
  const raw = line as Record<string, unknown>;
  if (!Array.isArray(raw.tint_details)) return [];

  const { glassTypes, tintPercentages } = getTintOptions(raw);
  const glassById = new Map(glassTypes.map((g) => [g.id, g.name]));
  const tintById = new Map(tintPercentages.map((p) => [p.id, p.name]));

  return raw.tint_details.flatMap((entry) => {
    if (!entry || typeof entry !== "object") return [];
    const detail = entry as Record<string, unknown>;
    const glass =
      String(detail.glass_type_name ?? "").trim() ||
      glassById.get(Number(detail.glass_type_id)) ||
      "";
    const tint =
      String(detail.tint_percentage_name ?? "").trim() ||
      tintById.get(Number(detail.tint_percentage_id)) ||
      "";
    // ponytail: unresolvable ids are dropped rather than shown raw.
    return glass && tint ? [`${glass}: ${tint}`] : [];
  });
}
